/**
 * When a window-level keyboard shortcut must stand down (`docs/CONVENTIONS.md`
 * "Keyboard"; spec §5 "shortcuts only when focus is outside inputs").
 *
 * The obvious half is text entry. The half that bites is buttons: a `keydown`
 * listener on `window` sees Enter and Space *before* the browser turns them
 * into a click on the focused control, so a shortcut handler that calls
 * `preventDefault()` silently swallows the activation. A keyboard user tabbing
 * to "Ge upp" and pressing Enter would submit their answer instead, and Space
 * on a flashcard rating button would do nothing at all.
 *
 * Only the two activation keys defer, and only to a control that handles them:
 * a letter shortcut (`n`, `h`, `1`–`4`) still works with a button focused,
 * which is where focus lands after any click.
 */

const TYPING_TAGS = ["INPUT", "TEXTAREA", "SELECT"];
const SELF_HANDLING = "a[href], button, summary, [role='button'], [role='link'], [contenteditable]";
/** Keys the browser turns into a click on the focused control. */
const ACTIVATION_KEYS = new Set(["Enter", " ", "Spacebar"]);

/** True when the event target is a text field the student is typing into. */
export function isTypingTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element) return false;
  return element.isContentEditable || TYPING_TAGS.includes(element.tagName ?? "");
}

/**
 * True when a window-level shortcut must not fire for this event: the student
 * is typing, is holding a modifier, a modal dialog is open, or pressed an
 * activation key while focused on a control that will act on it itself.
 */
export function shortcutsBlocked(event: KeyboardEvent): boolean {
  if (event.metaKey || event.ctrlKey || event.altKey) return true;
  // A window-level shortcut (`n`, `h`, `1`–`4`) does not stand down while an
  // `aria-modal` dialog is open — `Modal`'s own focus trap keeps Tab inside
  // it, but a letter or digit key still reaches this listener and both runs
  // the shortcut *and* moves focus out of the trap (e.g. `ReportIssue`'s
  // modal on the practice page). One dialog is open at a time in this app, so
  // a document-wide check is enough; every `Modal` sets this attribute.
  if (document.querySelector('[aria-modal="true"]')) return true;
  const target = event.target as HTMLElement | null;
  if (isTypingTarget(target)) return true;
  if (!ACTIVATION_KEYS.has(event.key)) return false;
  return !!target?.closest?.(SELF_HANDLING);
}
